import { BarChart3, Button, useDebounce, UserPlus, Users } from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LicenceGate } from "../../components/atoms/LicenceGate";
import { LicenceModal } from "../../components/molecules/LicenceModal/LicenceModal";
import { PageHeader } from "../../components/molecules/PageHeader";
//import { StaffAnalytics } from "../../components/molecules/StaffAnalytics"; //Temporarily hidden
import { facilitiesApi } from "@/services/facilities.api";
import { licenceApi } from "@/services/licence.api";
import { FacilityStats } from "@/types/facility.definition";
import { LicencePlan } from "@/types/licence.definition";
import { selectLicenceState } from "../../store/reducers/licenceSlice";
import { StaffForm } from "../../components/molecules/StaffForm";
import { StaffList } from "../../components/molecules/StaffList";
import { TabNavigation } from "../../components/molecules/TabNavigation";
import { useHasLicence } from "../../hooks/useHasLicence";
import useManager from "../../hooks/useManager";
import { doctorApi } from "../../services/doctor.api";
import { searchApi } from "../../services/search.api";
import secretaryApi from "../../services/secretary.api";
import { fetchStatsRequest } from "../../store/reducers/dashboardSlice";
import {
  CreateDoctorPayload,
  Doctor,
  ProvidedService,
} from "../../types/doctor.definition";
import {
  defaultSearchRequest,
  Order,
  Query,
} from "../../types/search.definition";
import { Specialty } from "../../types/specialty.definition";
import { Staff } from "../../types/staff.definition";
import { CivilityEnum, RoleEnum } from "../../types/user.definition";
import { convertToDateISOString } from "../../utils/date.utils";
import { exportStaffToCSV } from "../../utils/export.utils";

// export type Staff = Doctor; // Old definition - now using unified Staff type

export const ManageStaff: React.FC = () => {
  const { t } = useTranslation();
  const manager = useManager();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [staff, setStaff] = useState<Staff[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm);
  const [filterSpecialty, setFilterSpecialty] = useState<string[]>([]);
  //TODO V1
  //const [filterService, setFilterService] = useState<string[]>([]);
  const [filterRole, setFilterRole] = useState<"" | RoleEnum.DOCTOR | RoleEnum.SECRETARY>("");
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState<Staff | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [activeTab, setActiveTab] = useState("staff-list");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmittingStaff, setIsSubmittingStaff] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [facilityStaffStats, setfacilityStaffStats] =
    useState<FacilityStats | null>(null);
  const { licence } = useSelector(selectLicenceState);
  const hasLicence = useHasLicence();
  const [showLicenceModal, setShowLicenceModal] = useState(false);
  const [licencePlan, setLicencePlan] = useState<LicencePlan | null>(null);
  const [totalStaffCount, setTotalStaffCount] = useState(0);
  const facilityId = Number(manager.healthcareFacility?.id);

  // MED-1757: the quota counts active staff, like the API.
  const currentDoctorCount =
    facilityStaffStats?.summary?.metrics?.doctors?.active ?? 0;
  const currentSecretaryCount =
    facilityStaffStats?.summary?.metrics?.secretaries?.active ?? 0;
  const isDoctorLimitReached =
    licence !== null && currentDoctorCount >= licence.numberOfDoctors;
  const isSecretaryLimitReached =
    licence !== null && licence.maxSecretaries > 0 && currentSecretaryCount >= licence.maxSecretaries;

  // Search request state with pagination
  const [searchRequest, setSearchRequest] = useState({
    ...defaultSearchRequest,
    paginationSettings: {
      limit: itemsPerPage,
      offset: (currentPage - 1) * itemsPerPage,
    },
  });

  const loadStats = useCallback(async () => {
    if (!facilityId) return;
    const stats = await facilitiesApi.getFacilityStaffStats(facilityId);
    setfacilityStaffStats(stats);
  }, [facilityId]);

  //Load staff stats and licence plan
  useEffect(() => {
    if (!facilityId) return;
    loadStats();
  }, [facilityId, loadStats]);

  // Resolve the matching plan once the licence is available in the store
  useEffect(() => {
    if (!licence) return;
    licenceApi.getAvailablePlans().then((plans) => {
      const matchingPlan = plans.find((p) => p.type === licence.licenceType) ?? null;
      setLicencePlan(matchingPlan);
    }).catch(() => {});
  }, [licence]);

  // Update search request when pagination changes
  useEffect(() => {
    setSearchRequest((prev) => ({
      ...prev,
      paginationSettings: {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      },
    }));
  }, [currentPage, itemsPerPage]);

  // Fetch staff (doctors and secretaries)
  const fetchStaff = useCallback(async () => {
    if (!facilityId) {
      return;
    }

    // MED-1653: secretaries have no specialty, so a specialty filter means doctors only
    // (the API's specialty filter only applies to doctors).
    const effectiveRole =
      filterRole || (filterSpecialty.length > 0 ? RoleEnum.DOCTOR : "");

    const query: Query = {
      keyword: debouncedSearchTerm,
      roleName: effectiveRole || undefined,
      specialtyCodes: filterSpecialty,
      //TODO: V1
      //providedServiceCodes: filterService
    };

    const request = {
      ...searchRequest,
      sortBy: {
        ...searchRequest.sortBy,
        property: "user.firstName",
        order: Order.ASC,
      },
      query,
    };
    setIsLoading(true);
    // Fetch both doctors and secretaries
    const response = await searchApi.searchStaff(request, facilityId);

    const doctorsResponse =
      effectiveRole === "SECRETARY" ? [] : (response.doctors ?? []);
    const secretariesResponse =
      effectiveRole === RoleEnum.DOCTOR ? [] : (response.secretaries ?? []);

    setTotalStaffCount(response.paginationSettings?.totalCount ?? 0);
    setIsLoading(false);

    const combinedStaff = [...doctorsResponse, ...secretariesResponse];

    // Deduplicate based on unique email
    const uniqueStaff = Array.from(
      new Map(combinedStaff.map((member) => [member.email, member])).values(),
    );

    setStaff(uniqueStaff);
  }, [
    facilityId,
    debouncedSearchTerm,
    filterRole,
    filterSpecialty,
    searchRequest,
  ]);

  // Initial data fetch
  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
    //TODO V1 include //filterService
  }, [debouncedSearchTerm, filterSpecialty, filterRole]);

  // Filter staff based on current filters
  const filteredStaff = staff.filter((member) => {
    const searchTermLower = debouncedSearchTerm.toLowerCase();

    const matchesSearch =
      (member.firstName || "").toLowerCase().includes(searchTermLower) ||
      (member.lastName || "").toLowerCase().includes(searchTermLower) ||
      (member.email || "").toLowerCase().includes(searchTermLower) ||
      member.specialties?.some((s: { label: string }) =>
        (s.label || "").toLowerCase().includes(searchTermLower),
      );

    return matchesSearch;
  });

  const handleViewDetails = (staffMember: Staff) => {
    const role = staffMember.roleName === RoleEnum.DOCTOR ? "doctor" : "secretary";
    navigate(`/manager/staff/${role}/${staffMember.id}`, { state: { staff: staffMember } });
  };

  const handleEdit = (staffMember: Staff) => {
    setEditingStaff(staffMember);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingStaff(null);
    setSubmitSuccess(null);
  };

  const handleExportStaff = () => {
    if (filteredStaff.length === 0) {
      return;
    }
    exportStaffToCSV(filteredStaff);
  };

  const handleStaffSubmit = async (formData: Partial<CreateDoctorPayload>) => {
    setIsSubmittingStaff(true);
    setSubmitSuccess(null);
    // MED-1653: the modal (where submitSuccess is displayed) closes right after a
    // successful save, so the message is shown as a toast once it has closed.
    let successMessage: string | null = null;

    try {
      const isDoctor = formData.roleName === RoleEnum.DOCTOR;

      if (isDoctor && !editingStaff && isDoctorLimitReached) {
        setIsSubmittingStaff(false);
        return;
      }

      if (isDoctor) {
        // Prepare the doctor data with required fields for API
        const doctorData: CreateDoctorPayload = {
          firstName: formData.firstName || "",
          lastName: formData.lastName || "",
          refNumber: formData.refNumber || "",
          civility: formData.civility,
          email: formData.email || "",
          password: formData.password,
          roleName: RoleEnum.DOCTOR,
          photoUrl: formData.photoUrl,
          residency: "HOME",
          phoneNumber: formData.phoneNumber || "",
          paymentPhoneNumber: formData.paymentPhoneNumber,
          healthcareFacilityIds: [+manager.healthcareFacility?.id],
          preferredNotificationMethods:
            formData.preferredNotificationMethods || ["SMS", "EMAIL"],
          careerStartYear: convertToDateISOString(
            formData.careerStartYear as string,
          ),
          dateOfBirth: convertToDateISOString(formData.dateOfBirth as string),
          hospitalName: formData.hospitalName,
          diplomas: formData.diplomas,
          providedServices:
            (formData.providedServices as ProvidedService[]) || [],
        };

        if (editingStaff) {
          const updatePayload: Record<string, unknown> = {};

          if (formData.firstName !== editingStaff.firstName)
            updatePayload.firstName = formData.firstName;
          if (formData.lastName !== editingStaff.lastName)
            updatePayload.lastName = formData.lastName;
          if (formData.civility !== editingStaff.civility)
            updatePayload.civility = formData.civility;
          if (formData.phoneNumber !== editingStaff.phoneNumber)
            updatePayload.phoneNumber = formData.phoneNumber;
          if (formData.photoUrl !== editingStaff.photoUrl)
            updatePayload.photoUrl = formData.photoUrl;
          if (formData.hospitalName !== editingStaff.hospitalName)
            updatePayload.hospitalName = formData.hospitalName;
          if (formData.diplomas !== editingStaff.diplomas)
            updatePayload.diplomas = formData.diplomas;
          if (formData.refNumber !== editingStaff.refNumber)
            updatePayload.refNumber = formData.refNumber;
          if (formData.paymentPhoneNumber !== editingStaff.paymentPhoneNumber)
            updatePayload.paymentPhoneNumber = formData.paymentPhoneNumber;

          if (formData.dateOfBirth) {
            const formattedDob = convertToDateISOString(
              formData.dateOfBirth as string,
            );
            if (formattedDob !== editingStaff.dateOfBirth)
              updatePayload.dateOfBirth = formattedDob;
          }

          if (formData.careerStartYear) {
            const formattedCareer = convertToDateISOString(
              formData.careerStartYear as string,
            );
            if (formattedCareer !== editingStaff.careerStartYear)
              updatePayload.careerStartYear = formattedCareer;
          }

          if (
            JSON.stringify(formData.preferredNotificationMethods) !==
            JSON.stringify(editingStaff.preferredNotificationMethods)
          ) {
            updatePayload.preferredNotificationMethods =
              formData.preferredNotificationMethods;
          }

          if (
            JSON.stringify(formData.providedServices) !==
            JSON.stringify(editingStaff.providedServices)
          ) {
            updatePayload.providedServices = formData.providedServices;
          }

          const specialtyIds = ((formData.specialties || []) as Specialty[])
            .map((s) => (s.id ? Number(s.id) : null))
            .filter((id): id is number => id !== null);

          const existingSpecialtyIds = (
            (editingStaff.specialties || []) as Specialty[]
          )
            .map((s) => (s.id ? Number(s.id) : null))
            .filter((id): id is number => id !== null);

          if (
            JSON.stringify(specialtyIds.sort()) !==
            JSON.stringify(existingSpecialtyIds.sort())
          ) {
            updatePayload.specialtyIds = specialtyIds;
          }

          await doctorApi.updateDoctor(editingStaff.id, updatePayload);
          successMessage = (
            t("staff.manage.form.success.doctorUpdated", {
              name: `${formData.firstName} ${formData.lastName}`,
            })
          );
        } else {
          const specialtiesForCreate = (
            (formData.specialties || []) as Specialty[]
          ).map((s) => ({
            id: s.id != null ? Number(s.id) : undefined,
            code: s.code,
            label: s.label,
          }));

          await doctorApi.createDoctor(facilityId, {
            ...doctorData,
            specialties: specialtiesForCreate,
          } as unknown as Doctor);

          successMessage = (
            t("staff.manage.form.success.doctorCreated", {
              name: `${formData.firstName} ${formData.lastName}`,
            })
          );
        }
      } else {
        const secretaryData = {
          firstName: (formData.firstName || "") as string,
          lastName: (formData.lastName || "") as string,
          email: (formData.email || "") as string,
          civility: (formData.civility as string) || CivilityEnum.MR,
          password: formData.password as string | undefined,
          roleName: "SECRETARY" as string,
          healthcareFacilityId: +manager.healthcareFacility?.id,
          phoneNumber: (formData.phoneNumber || "") as string,
          preferredNotificationMethods:
            (formData.preferredNotificationMethods || [
              "EMAIL",
              "SMS",
            ]) as string[],
          dateOfBirth: convertToDateISOString(formData.dateOfBirth as string),
        };

        if (editingStaff) {
          const updateData: Record<string, unknown> = {};

          if (formData.firstName !== editingStaff.firstName)
            updateData.firstName = formData.firstName;
          if (formData.lastName !== editingStaff.lastName)
            updateData.lastName = formData.lastName;
          if (formData.civility !== editingStaff.civility)
            updateData.civility = formData.civility;
          if (formData.phoneNumber !== editingStaff.phoneNumber)
            updateData.phoneNumber = formData.phoneNumber;
          if (formData.photoUrl !== editingStaff.photoUrl)
            updateData.photoUrl = formData.photoUrl;

          if (formData.dateOfBirth) {
            const formattedDob = convertToDateISOString(
              formData.dateOfBirth as string,
            );
            if (formattedDob !== editingStaff.dateOfBirth)
              updateData.dateOfBirth = formattedDob;
          }

          if (
            JSON.stringify(formData.preferredNotificationMethods) !==
            JSON.stringify(editingStaff.preferredNotificationMethods)
          ) {
            updateData.preferredNotificationMethods =
              formData.preferredNotificationMethods;
          }

          updateData.healthcareFacilityId = +manager.healthcareFacility?.id;

          await secretaryApi.updateSecretary(editingStaff.id, updateData);

          successMessage = (
            t("staff.manage.form.success.secretaryUpdated", {
              name: `${formData.firstName} ${formData.lastName}`,
            })
          );
        } else {
          await secretaryApi.createSecretary(secretaryData);

          successMessage = (
            t("staff.manage.form.success.secretaryCreated", {
              name: `${formData.firstName} ${formData.lastName}`,
            })
          );
        }
      }

      // -------------------------
      // SUCCESS-ONLY ACTIONS
      // -------------------------
      await fetchStaff();
      await loadStats();

      if (manager.healthcareFacility?.id) {
        dispatch(
          fetchStatsRequest({ facilityId: manager.healthcareFacility.id }),
        );
      }

      handleCloseModal();
      if (successMessage) toast.success(successMessage);
    } catch (error) {
      console.error("Error submitting staff:", error);
      // Do NOT close modal or refresh lists on error
    } finally {
      setIsSubmittingStaff(false); // Only once
    }
  };

  // Staff List Tab Content
  const StaffListContent = (
    <StaffList
      facilityStaffStats={facilityStaffStats}
      licence={licence}
      isLoading={isLoading}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      debouncedSearchTerm={debouncedSearchTerm}
      filterSpecialty={filterSpecialty}
      //TODO V1 add filterService
      //filterService={filterService}
      //setFilterService={setFilterService}
      filterRole={filterRole}
      setFilterRole={setFilterRole}
      showFilters={showFilters}
      setShowFilters={setShowFilters}
      viewMode={viewMode}
      setViewMode={setViewMode}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      itemsPerPage={itemsPerPage}
      setItemsPerPage={setItemsPerPage}
      totalStaffCount={totalStaffCount}
      filteredStaff={filteredStaff}
      onEditStaff={handleEdit}
      onViewDetails={handleViewDetails}
      setFilterSpecialty={setFilterSpecialty}
    />
  );

  // AI Analytics Tab Content (Temporarily hidden)
  // const AiAnalyticsContent = <StaffAnalytics staff={staff} />;

  return (
    <div className="p-6 space-y-6 min-h-screen bg-neutral-50">
      {/* Page Header */}
      <PageHeader
        title={t("staff.manage.title")}
        description={t("staff.manage.subtitle")}
        icon={Users}
        actions={
          <div className="flex items-center space-x-3">
            <Button variant="ghost" size="sm" onClick={handleExportStaff}>
              <BarChart3 className="mr-2 w-4 h-4" />
              {t("staff.manage.reports")}
            </Button>
            <LicenceGate
              isLicenced={hasLicence}
              tooltipText={t("licence.requiredTooltip")}
              onRestrictedClick={() => setShowLicenceModal(true)}
            >
              <Button
                size="sm"
                onClick={() => {
                  setShowAddModal(true);
                  setSubmitSuccess(null);
                }}
              >
                <UserPlus className="mr-2 w-4 h-4" />
                {t("staff.manage.addStaff")}
              </Button>
            </LicenceGate>
          </div>
        }
      />

      <TabNavigation
        tabs={[
          {
            id: "staff-list",
            label: t("staff.manage.staffList"),
            icon: Users,
            content: StaffListContent,
          },
          // Temporarily hidden - AI Analytics
          // {
          //   id: "ai-analytics",
          //   label: t("staff.manage.aiAnalytics"),
          //   icon: BarChart3,
          //   content: AiAnalyticsContent,
          // },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Add/Edit Staff Modal */}
      <StaffForm
        isOpen={showAddModal}
        onClose={handleCloseModal}
        onSubmit={handleStaffSubmit}
        editingStaff={editingStaff}
        isSubmitting={isSubmittingStaff}
        successMessage={submitSuccess}
        isDoctorLimitReached={isDoctorLimitReached}
        isSecretaryLimitReached={isSecretaryLimitReached}
        currentDoctorCount={currentDoctorCount}
        maxDoctors={licence?.numberOfDoctors}
        currentSecretaryCount={currentSecretaryCount}
        maxSecretaries={licence?.maxSecretaries}
        paymentDueDate={licence?.paymentDueDate ?? null}
        pricePerDoctor={licencePlan?.pricePerDoctor}
        facilityId={facilityId}
        onAssignSuccess={async () => {
          handleCloseModal();
          await fetchStaff();
          await loadStats();
          if (manager.healthcareFacility?.id) {
            dispatch(fetchStatsRequest({ facilityId: manager.healthcareFacility.id }));
          }
        }}
        onUpgradeLicence={() => {
          handleCloseModal();
          navigate("/manager/licence");
        }}
      />

      <LicenceModal
        isOpen={showLicenceModal}
        onClose={() => setShowLicenceModal(false)}
      />
    </div>
  );
};
